import React from "react";
import { ShieldCheck, MapPin, Sparkles } from "lucide-react";

/**
 * "Your assigned advisor" trust card — shown on Results and Booking pages
 * right before/next to the booking CTA. Builds rapport before the call.
 *
 * Props:
 *   advisor — { name, photo_url, bio, license_states[], phone, calendly_link, slug }
 *   compact — bool · use a smaller layout suitable for booking page sidebar
 *   tone    — "light" (default, white card on light backgrounds) | "dark" (navy card)
 */
export default function AdvisorCard({ advisor, compact = false, tone = "light" }) {
    if (!advisor || !advisor.name) return null;
    const isDark = tone === "dark";
    const cardClass = isDark
        ? "bg-navy-900 border-amber-400/30 text-white"
        : "bg-white border-slate-200 text-slate-900";
    const eyebrowClass = isDark ? "text-amber-300" : "text-amber-700";
    const subTextClass = isDark ? "text-blue-100/80" : "text-slate-600";
    const badgeClass = isDark
        ? "bg-amber-400/15 border-amber-400/40 text-amber-200"
        : "bg-amber-50 border-amber-200 text-amber-800";

    const initials = (advisor.name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
    const photoW = compact ? "w-24" : "w-32 md:w-36";

    return (
        <div className={`border-2 rounded-2xl p-5 md:p-6 ${cardClass}`} data-testid="advisor-card">
            <div className={`text-[11px] uppercase tracking-[0.22em] font-bold ${eyebrowClass} mb-4 flex items-center gap-1.5`}>
                <ShieldCheck className="w-3.5 h-3.5" />
                Your assigned advisor
            </div>
            <div className={`flex ${compact ? "flex-col items-center text-center" : "flex-col sm:flex-row sm:items-start"} gap-5`}>
                {advisor.photo_url ? (
                    <img
                        src={advisor.photo_url}
                        alt={advisor.name}
                        className={`${photoW} aspect-[3/4] rounded-xl object-cover object-top border ${isDark ? "border-white/20" : "border-slate-200"} shadow-md shrink-0`}
                        data-testid="advisor-photo"
                    />
                ) : (
                    <div className={`${photoW} aspect-[3/4] rounded-xl flex items-center justify-center font-serif text-4xl ${isDark ? "bg-amber-400/20 text-amber-200" : "bg-slate-100 text-slate-700"} shrink-0`}>
                        {initials}
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="font-serif text-2xl leading-tight" data-testid="advisor-name">{advisor.name}</div>
                    <div className={`mt-1 text-xs uppercase tracking-wider ${subTextClass}`}>Licensed Insurance Professional</div>
                    {advisor.license_states && advisor.license_states.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                            {advisor.license_states.map((s) => (
                                <span key={s} className={`inline-flex items-center gap-1 text-[11px] font-semibold border rounded-full px-2 py-0.5 ${badgeClass}`}>
                                    <MapPin className="w-3 h-3" />
                                    {s}
                                </span>
                            ))}
                        </div>
                    )}
                    {!compact && advisor.bio && (
                        <p className={`mt-4 text-sm leading-relaxed ${subTextClass}`} data-testid="advisor-bio">
                            {advisor.bio}
                        </p>
                    )}
                    <div className={`mt-4 inline-flex items-center gap-1.5 text-xs font-semibold ${eyebrowClass}`}>
                        <Sparkles className="w-3 h-3" /> Personally reviewing your contract
                    </div>
                </div>
            </div>
        </div>
    );
}
